import React from 'react'

import { Minus } from '@assets/controls/minus'
import { Plus } from '@assets/controls/plus'
import { counterStyles } from '@components/shared/counter/counter.styles'

const { Container, ControlButton, ValueField } = counterStyles

export type CounterProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange'
> & {
  testId?: string
  max?: number
  min?: number
  value?: number
  disabled?: boolean
  onChange?: (value: number) => void
}

export function Counter({
  testId,
  value = 0,
  min = 0,
  max,
  disabled = false,
  onChange,
  ...props
}: CounterProps) {
  const [internalValue, setInternalValue] = React.useState<string>(
    value.toString()
  )

  const update = (
    type: 'increment' | 'decrement' | 'input',
    inputValue?: number
  ) => {
    if (type === 'input' && inputValue === undefined) {
      setInternalValue(inputValue ?? '')
      return
    }

    const { [type]: updatedValue } = {
      decrement: value - 1,
      increment: value + 1,
      input: inputValue === undefined ? value : inputValue,
    }

    if (updatedValue < min || (max !== undefined && updatedValue > max)) {
      return
    }

    setInternalValue(updatedValue.toString())
    onChange?.(updatedValue)
  }

  const disableNonNumericInput: React.KeyboardEventHandler<HTMLInputElement> = (
    e
  ) => {
    if (!/[0-9]/.test(e.key) && !e.ctrlKey && !e.metaKey) {
      e.preventDefault()
    }
  }

  return (
    <Container data-testid={testId} {...props}>
      <ControlButton
        data-testid={testId && `${testId}_decrease`}
        disabled={disabled || value <= min}
        onClick={() => update('decrement')}
      >
        <Minus />
      </ControlButton>

      <ValueField
        placeholder={value.toString()}
        value={internalValue}
        disabled={disabled}
        onKeyPress={disableNonNumericInput}
        onChange={({ target: { value: inputValue } }) =>
          update('input', inputValue === '' ? undefined : Number(inputValue))
        }
      />

      <ControlButton
        data-testid={testId && `${testId}_increase`}
        disabled={disabled || value >= (max ?? value + 1)}
        onClick={() => update('increment')}
      >
        <Plus />
      </ControlButton>
    </Container>
  )
}
